import { useEffect, useState } from "react";
import { WifiOff, RefreshCw } from "lucide-react";
import { useI18n } from "@/lib/i18n.jsx";

/**
 * OfflineBanner — global banner that appears when the device loses
 * network connectivity, so users on the go (elevator, tunnel, weak Wi-Fi)
 * understand *why* a save/log/coach-reply just failed instead of assuming
 * the app itself is broken.
 *
 * Signals we listen to:
 *  1. `window.online` / `window.offline` — fires when the OS toggles the
 *     radio state. Instant but not always reliable on Chrome mobile.
 *  2. `calofay:network-error` — dispatched by our axios response interceptor
 *     when a request fails with no `err.response` (true network failure,
 *     not a 4xx/5xx from the server). This catches the cases where the OS
 *     still thinks it's "online" but the server is unreachable (captive
 *     portal, DNS hiccup, dropped Wi-Fi that hasn't renegotiated yet).
 *  3. `calofay:network-ok` — dispatched by the interceptor on the first
 *     successful response after an outage, so we can auto-dismiss the
 *     banner without waiting for the OS `online` event.
 *
 * UX choice: we intentionally do NOT toast on re-connect. The banner
 * silently disappearing is enough of a signal, and the previous toast
 * was firing on false-positive blips (cold-start axios timeout that
 * recovered on its own) which felt spammy to users.
 */
export default function OfflineBanner() {
  const { lang } = useI18n();
  const en = lang === "en";
  const [offline, setOffline] = useState(() =>
    typeof navigator !== "undefined" && navigator.onLine === false
  );

  useEffect(() => {
    const goOffline = () => setOffline(true);
    const goOnline = () => setOffline(false);

    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);
    window.addEventListener("calofay:network-error", goOffline);
    window.addEventListener("calofay:network-ok", goOnline);
    return () => {
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("online", goOnline);
      window.removeEventListener("calofay:network-error", goOffline);
      window.removeEventListener("calofay:network-ok", goOnline);
    };
  }, []);

  if (!offline) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      dir={en ? "ltr" : "rtl"}
      data-testid="offline-banner"
      className="fixed inset-x-0 z-[100] pointer-events-none flex justify-center px-3"
      style={{
        // Sit below the notch by adding the safe-area inset. Without
        // this the banner would slide UNDER the status bar because
        // the WebView now extends edge-to-edge (`contentInset: never`).
        top: "env(safe-area-inset-top)",
        paddingTop: 8,
      }}
    >
      <div className="pointer-events-auto flex items-center gap-2 max-w-sm w-full bg-neutral-900 text-white shadow-lg rounded-2xl px-4 py-2.5 border border-white/10 backdrop-blur">
        <WifiOff className="w-4 h-4 text-amber-400 flex-shrink-0" />
        <div className="flex-1 text-[13px] leading-tight font-medium">
          {en
            ? "No internet connection — some features aren't available"
            : "لا يوجد اتصال بالإنترنت — بعض المزايا لا تعمل حالياً"}
        </div>
        <button
          onClick={() => {
            // Manual retry: try to fetch a tiny endpoint. If it succeeds,
            // the axios interceptor will fire `calofay:network-ok` and hide
            // this banner. If it still fails, banner stays.
            fetch(`${process.env.REACT_APP_BACKEND_URL}/api/health`, {
              cache: "no-store",
            })
              .then((r) => {
                if (r.ok) window.dispatchEvent(new Event("calofay:network-ok"));
              })
              .catch(() => {});
          }}
          className="flex-shrink-0 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors active:scale-95"
          aria-label={en ? "Retry" : "إعادة المحاولة"}
          data-testid="offline-retry-btn"
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
